import {
  FETCH_CUSTOMER_TRANSACTIONS_RESOLVED,
  FETCH_CUSTOMER_TRANSACTIONS_FAILED,
  FETCH_CASHBOX_TRANSACTION_DETAILS_RESOLVED,
  FETCH_CASHBOX_TRANSACTION_DETAILS_FAILED,
  FETCH_IMPORT_TRANSACTION_DETAILS_RESOLVED,
  FETCH_IMPORT_TRANSACTION_DETAILS_FAILED,
  TOGGLE_TRANSACTION_EXPANDED,
  CLEAR_TRANSACTION_HISTORY,
  FETCH_CUSTOMER_TRANSACTIONS_SUMMARY_RESOLVED
} from "../ActionTypes/transactionHistory";

const initialState = {
  transactions: [],
  currencies: [], // All currencies for the customer (for consistent columns)
  openingBalance: [], // Opening balance for the date range
  closingBalance: [], // Closing balance for the date range
  thresholdDate: null, // Threshold date for this customer
  pagination: {
    currentPage: 1,
    lastPage: 1,
    perPage: 15,
    total: 0
  },
  cashboxDetails: {}, // { [cashboxId]: detailsData }
  importDetails: {}, // { [importKey]: detailsData } where importKey = "YYYY-MM-DD_cashboxId" or "YYYY-MM-DD_excluded"
  expandedTransactions: [], // Array of expanded transaction IDs
  summary: null,
  loading: false,
  error: null
};

const transactionHistoryReducer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH_CUSTOMER_TRANSACTIONS_RESOLVED:
      // Only update currencies if provided (first page only), otherwise keep existing
      const newCurrencies = action.payload.meta?.currencies;
      // Handle new nested response format: data.transactions instead of data directly
      const responseData = action.payload.data || {};
      const transactions = responseData.transactions || responseData || [];
      return {
        ...state,
        transactions: Array.isArray(transactions) ? transactions : [],
        currencies: newCurrencies ? newCurrencies : state.currencies,
        openingBalance: responseData.opening_balance || state.openingBalance,
        closingBalance: responseData.closing_balance || state.closingBalance,
        thresholdDate: responseData.threshold_date || state.thresholdDate,
        pagination: {
          currentPage: action.payload.meta?.pagination?.current_page || 1,
          lastPage: action.payload.meta?.pagination?.total_pages || 1,
          perPage: action.payload.meta?.pagination?.per_page || 15,
          total: action.payload.meta?.pagination?.total || 0
        },
        loading: false,
        error: null
      };

    case FETCH_CUSTOMER_TRANSACTIONS_FAILED:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    case FETCH_CASHBOX_TRANSACTION_DETAILS_RESOLVED:
      // Merge tab data into existing cashbox details
      const existingDetails = state.cashboxDetails[action.cashboxId] || {};
      const newPayload = action.payload;
      const tab = action.tab;

      // Build merged details - keep common fields, store tab-specific data
      const mergedDetails = {
        ...existingDetails,
        cashbox: newPayload.cashbox || existingDetails.cashbox,
        totals: newPayload.totals || existingDetails.totals,
        summary: newPayload.summary || existingDetails.summary,
      };

      // Store tab-specific data
      // API returns pagination as: { collections: {...}, delivery_fees: {...}, extra_fees: {...} }
      const pagination = newPayload.pagination || {};
      if (tab === 'collections') {
        mergedDetails.collections = newPayload.collections || [];
        mergedDetails.collections_pagination = pagination.collections || pagination;
      } else if (tab === 'delivery_fees') {
        mergedDetails.delivery_fees = newPayload.delivery_fees || [];
        mergedDetails.delivery_fees_pagination = pagination.delivery_fees || pagination;
      } else if (tab === 'extra_fees') {
        mergedDetails.extra_fees = newPayload.extra_fees || [];
        mergedDetails.extra_fees_pagination = pagination.extra_fees || pagination;
      } else {
        // No tab specified - store all data (backward compatibility)
        mergedDetails.collections = newPayload.collections || [];
        mergedDetails.delivery_fees = newPayload.delivery_fees || [];
        mergedDetails.extra_fees = newPayload.extra_fees || [];
        mergedDetails.collections_pagination = pagination.collections;
        mergedDetails.delivery_fees_pagination = pagination.delivery_fees;
        mergedDetails.extra_fees_pagination = pagination.extra_fees;
      }

      return {
        ...state,
        cashboxDetails: {
          ...state.cashboxDetails,
          [action.cashboxId]: mergedDetails
        }
      };

    case FETCH_CASHBOX_TRANSACTION_DETAILS_FAILED:
      return {
        ...state,
        cashboxDetails: {
          ...state.cashboxDetails,
          [action.cashboxId]: { error: action.payload }
        }
      };

    case FETCH_IMPORT_TRANSACTION_DETAILS_RESOLVED:
      // Merge tab data into existing import details
      const existingImportDetails = state.importDetails[action.importKey] || {};
      const newImportPayload = action.payload;
      const importTab = action.tab;

      // Build merged details
      const mergedImportDetails = {
        ...existingImportDetails,
        import_date: newImportPayload.import_date || existingImportDetails.import_date,
        cashbox_id: newImportPayload.cashbox_id || existingImportDetails.cashbox_id,
        totals: newImportPayload.totals || existingImportDetails.totals,
        summary: newImportPayload.summary || existingImportDetails.summary,
      };

      // Store tab-specific data
      const importPagination = newImportPayload.pagination || {};
      if (importTab === 'collections') {
        mergedImportDetails.collections = newImportPayload.collections || [];
        mergedImportDetails.collections_pagination = importPagination.collections || importPagination;
      } else if (importTab === 'delivery_fees') {
        mergedImportDetails.delivery_fees = newImportPayload.delivery_fees || [];
        mergedImportDetails.delivery_fees_pagination = importPagination.delivery_fees || importPagination;
      } else if (importTab === 'extra_fees') {
        mergedImportDetails.extra_fees = newImportPayload.extra_fees || [];
        mergedImportDetails.extra_fees_pagination = importPagination.extra_fees || importPagination;
      } else {
        // No tab specified - store all data
        mergedImportDetails.collections = newImportPayload.collections || [];
        mergedImportDetails.delivery_fees = newImportPayload.delivery_fees || [];
        mergedImportDetails.extra_fees = newImportPayload.extra_fees || [];
        mergedImportDetails.collections_pagination = importPagination.collections;
        mergedImportDetails.delivery_fees_pagination = importPagination.delivery_fees;
        mergedImportDetails.extra_fees_pagination = importPagination.extra_fees;
      }

      return {
        ...state,
        importDetails: {
          ...state.importDetails,
          [action.importKey]: mergedImportDetails
        }
      };

    case FETCH_IMPORT_TRANSACTION_DETAILS_FAILED:
      return {
        ...state,
        importDetails: {
          ...state.importDetails,
          [action.importKey]: { error: action.payload }
        }
      };

    case TOGGLE_TRANSACTION_EXPANDED:
      const isExpanded = state.expandedTransactions.includes(action.transactionId);
      return {
        ...state,
        expandedTransactions: isExpanded
          ? state.expandedTransactions.filter(id => id !== action.transactionId)
          : [...state.expandedTransactions, action.transactionId]
      };

    case CLEAR_TRANSACTION_HISTORY:
      return {
        ...initialState
      };

    case FETCH_CUSTOMER_TRANSACTIONS_SUMMARY_RESOLVED:
      return {
        ...state,
        summary: action.payload
      };

    default:
      return state;
  }
};

export default transactionHistoryReducer;
